import { DocumentTextIcon, CloudArrowUpIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '@stereograph/teia-system-design/components/Button';
import { Icon } from '@stereograph/teia-system-design/components/Icon';
import CodeMirror, { EditorView } from '@uiw/react-codemirror';
import { json } from '@codemirror/lang-json';
import { jsonSchema } from 'codemirror-json-schema';
import { KeyboardEvent, useState } from 'react';
import { Box } from '@stereograph/teia-system-design/components/Box';
import { SxProps } from '@mui/material/styles';

export interface AliasTableEditorProps {
  sx?: SxProps;
  aliasTableValue: string;
  onChange: (value: string) => void;
  onPost: () => void;
  onDelete: () => void;
  schema?: any;
  id: string | null;
  isLoading: boolean;
}

export const TableAliasEditor = (props: AliasTableEditorProps) => {
  const { aliasTableValue, onChange, onPost, onDelete, sx, schema, id, isLoading } = props;
  const [valueChanged, setValueChanged] = useState<boolean>(false);

  const onCodeChange = (value: string) => {
    onChange(value);
    setValueChanged(true);
  };

  const onButtonPost = () => {
    onPost();
    setValueChanged(false);
  };

  const onButtonDelete = () => {
    onDelete();
    setValueChanged(false);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    e.stopPropagation();
  };

  const onFormat = () => {
    const data = JSON.parse(aliasTableValue);
    onChange(JSON.stringify(data, null, 2));
  };

  return (
    <Box
      sx={{
        ...sx,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'scroll',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {!!id && (
          <Box>
            ID:
            {id}
          </Box>
        )}
        <Box
          sx={{
            flexGrow: 1,
          }}
        />
        {!!id && (
          <Button
            onClick={onButtonDelete}
            aria-label="Delete Alias"
            color="error"
            variant="text"
            disabled={isLoading}
          >
            <Icon component={TrashIcon} />
            Delete Alias
          </Button>
        )}
        <Button
          onClick={onButtonPost}
          aria-label="Post Alias"
          color="primary"
          variant="text"
          disabled={isLoading || !valueChanged}
        >
          <Icon component={CloudArrowUpIcon} />
          Post Alias
        </Button>
        <Button onClick={onFormat} aria-label="Format" color="info" variant="text">
          <Icon component={DocumentTextIcon} />
          Format JSON
        </Button>
      </Box>
      <CodeMirror
        style={{
          border: '1px solid lightgray',
          height: '100%',
          width: '100%',
        }}
        tabIndex={0}
        theme="light"
        value={aliasTableValue}
        onChange={onCodeChange}
        onKeyDown={onKeyDown}
        extensions={[json(), jsonSchema(schema), EditorView.lineWrapping]}
        basicSetup={{
          foldGutter: true,
          dropCursor: false,
          allowMultipleSelections: true,
          indentOnInput: true,
          autocompletion: true,
          bracketMatching: true,
          closeBrackets: true,
        }}
      />
    </Box>
  );
};
